import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  increment, 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  getDocs,
  arrayUnion,
  arrayRemove,
  serverTimestamp 
} from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { db, analytics, isFirebaseAvailable } from '../config/firebase';

// Enhanced error handling wrapper
const withErrorHandling = async (operation, fallback = null, operationName = 'Firebase operation') => {
  if (!isFirebaseAvailable()) {
    console.warn(`${operationName} skipped - Firebase not available`);
    return fallback;
  }
  
  try {
    return await operation();
  } catch (error) {
    console.error(`${operationName} failed:`, error);
    
    // Log error to analytics if available
    if (analytics) {
      logEvent(analytics, 'firebase_error', {
        operation: operationName,
        error_message: error.message
      });
    }
    
    // Return fallback value instead of throwing
    return fallback;
  }
};

// User Profile Management
export const createUserProfile = async (walletAddress) => {
  return withErrorHandling(async () => {
    const userRef = doc(db, 'users', walletAddress.toLowerCase());
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        walletAddress: walletAddress.toLowerCase(),
        bookmarkedCampaigns: [],
        preferences: {
          notifications: true,
          emailUpdates: false
        },
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp()
      });
      return { created: true };
    } else {
      // Update last active
      await updateDoc(userRef, {
        lastActive: serverTimestamp()
      });
      return { created: false, updated: true };
    }
  }, { created: false, error: true }, 'createUserProfile');
};

export const getUserProfile = async (walletAddress) => {
  return withErrorHandling(async () => {
    const userRef = doc(db, 'users', walletAddress.toLowerCase());
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data() : null;
  }, null, 'getUserProfile');
};

// Campaign Analytics
export const trackCampaignView = async (campaignId) => {
  return withErrorHandling(async () => {
    const campaignRef = doc(db, 'campaignAnalytics', campaignId);
    
    try {
      await updateDoc(campaignRef, {
        views: increment(1),
        lastViewed: serverTimestamp()
      });
    } catch (error) {
      // If document doesn't exist, create it
      if (error.code === 'not-found') {
        await setDoc(campaignRef, {
          views: 1,
          bookmarks: 0,
          shares: 0,
          createdAt: serverTimestamp(),
          lastViewed: serverTimestamp()
        });
      } else {
        throw error;
      }
    }
    
    // Also log to Firebase Analytics
    if (analytics) {
      logEvent(analytics, 'campaign_view', {
        campaign_id: campaignId
      });
    }
    
    return { success: true };
  }, { success: false }, 'trackCampaignView');
};

export const getCampaignAnalytics = async (campaignId) => {
  return withErrorHandling(async () => {
    const campaignRef = doc(db, 'campaignAnalytics', campaignId);
    const campaignDoc = await getDoc(campaignRef);
    return campaignDoc.exists() ? campaignDoc.data() : {
      views: 0,
      bookmarks: 0,
      shares: 0
    };
  }, { views: 0, bookmarks: 0, shares: 0 }, 'getCampaignAnalytics');
};

// Bookmark Management
export const toggleBookmark = async (walletAddress, campaignId) => {
  return withErrorHandling(async () => {
    const userRef = doc(db, 'users', walletAddress.toLowerCase());
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const isBookmarked = userData.bookmarkedCampaigns?.includes(campaignId);
      
      if (isBookmarked) {
        // Remove bookmark
        await updateDoc(userRef, {
          bookmarkedCampaigns: arrayRemove(campaignId)
        });
        
        // Decrease bookmark count
        const campaignRef = doc(db, 'campaignAnalytics', campaignId);
        await updateDoc(campaignRef, {
          bookmarks: increment(-1)
        });
        
        return false; // Not bookmarked anymore
      } else {
        // Add bookmark
        await updateDoc(userRef, {
          bookmarkedCampaigns: arrayUnion(campaignId)
        });
        
        // Increase bookmark count
        const campaignRef = doc(db, 'campaignAnalytics', campaignId);
        await updateDoc(campaignRef, {
          bookmarks: increment(1)
        });
        
        return true; // Now bookmarked
      }
    }
    return false;
  }, false, 'toggleBookmark');
};

export const isBookmarked = async (walletAddress, campaignId) => {
  return withErrorHandling(async () => {
    const userProfile = await getUserProfile(walletAddress);
    return userProfile?.bookmarkedCampaigns?.includes(campaignId) || false;
  }, false, 'isBookmarked');
};

// Activity Feed
export const addActivity = async (type, data) => {
  return withErrorHandling(async () => {
    await addDoc(collection(db, 'activities'), {
      type, // 'donation', 'campaign_created', 'goal_reached', etc.
      ...data,
      timestamp: serverTimestamp()
    });
    
    // Log to Firebase Analytics
    if (analytics) {
      logEvent(analytics, 'platform_activity', {
        activity_type: type,
        campaign_id: data.campaignId
      });
    }
    
    return { success: true };
  }, { success: false }, 'addActivity');
};

export const getRecentActivities = async (limitCount = 10) => {
  return withErrorHandling(async () => {
    const q = query(
      collection(db, 'activities'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }, [], 'getRecentActivities');
};

// Platform Analytics
export const getPlatformStats = async () => {
  return withErrorHandling(async () => {
    // Get total analytics data
    const analyticsQuery = query(collection(db, 'campaignAnalytics'));
    const analyticsSnapshot = await getDocs(analyticsQuery);
    
    let totalViews = 0;
    let totalBookmarks = 0;
    
    analyticsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      totalViews += data.views || 0;
      totalBookmarks += data.bookmarks || 0;
    });
    
    // Get user count
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);
    
    return {
      totalViews,
      totalBookmarks,
      totalUsers: usersSnapshot.size,
      totalCampaignsTracked: analyticsSnapshot.size
    };
  }, {
    totalViews: 0,
    totalBookmarks: 0,
    totalUsers: 0,
    totalCampaignsTracked: 0
  }, 'getPlatformStats');
};

// Track campaign shares
export const trackCampaignShare = async (campaignId) => {
  return withErrorHandling(async () => {
    const campaignRef = doc(db, 'campaignAnalytics', campaignId);
    await updateDoc(campaignRef, {
      shares: increment(1)
    });
    
    if (analytics) {
      logEvent(analytics, 'campaign_share', {
        campaign_id: campaignId
      });
    }
    
    return { success: true };
  }, { success: false }, 'trackCampaignShare');
};